<template>
  <c-sidesheet id="inventory-category-modal" :title="title" :active="active" @close="close">
    <div v-if="loading">Loading...</div>

    <div v-else class="inventory-category">
      <div class="inventory-category__form">
        <div class="row">
          <div class="col col-xl-6">
            <div
              class="form-group inventory-category__name"
              :class="{
                'is-invalid': isSubmitted && nameError,
              }"
            >
              <c-input
                v-model="form.name"
                floating-label="Наименование"
                :clearable="true"
                :max-length="500"
                :required-mark="true"
                :required="isSubmitted && nameError"
                :required-message="isSubmitted && nameError"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col col-xl-6">
            <div class="form-group inventory-category__category">
              <c-input
                v-model="form.parentId"
                type="select"
                :items="categoriesList"
                label="Местоположение"
                placeholder="Верхний уровень"
                :clearable="true"
                :searchable="true"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col col-xl-6">
            <div class="form-group inventory-category__responsible">
              <c-input
                v-model="form.responsible"
                type="select"
                :items="workersList"
                floating-label="Ответственный"
                :clearable="true"
                :searchable="true"
                :with-categorized="true"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col col-xl-10">
            <div class="form-group inventory-category__description">
              <c-textarea v-model="form.description" :clearable="true" floating-label="Описание" max-length="500" />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col col-xl-10">
            <div
              class="form-group inventory-category__link"
              :class="{
                'is-invalid': isSubmitted && linkError,
              }"
            >
              <c-input
                v-model="form.link"
                floating-label="Ссылка"
                :clearable="true"
                :max-length="500"
                :required="isSubmitted && linkError"
                :required-message="isSubmitted && linkError"
              />
            </div>
          </div>
        </div>

        <div class="form-group inventory-category__files">
          <file-loader
            ref="loader"
            v-model="files"
            format="images,pdf,docs,video"
            actions="loadFiles,preview"
            :links="fileLinks"
            :limit="10"
            @changeLinks="fileLinks = $event"
          >
            <template #note>
              <div class="note">
                Допустимые форматы изображений: .jpeg, .jpg, .gif, .png, видео: .mp4, .avi, .wmv, .mov, .3gp,
                .flv, .mpeg-1,2, .webm. Размер файла — не более 10Мб.
              </div>
            </template>
          </file-loader>
        </div>
      </div>

      <div
        class="inventory-category__actions"
        :class="{
          fixed: fixFooter,
        }"
      >
        <c-button class="inventory-category__reset" label="Отменить" size="large" @click="close" />
        <c-button
          class="inventory-category__save"
          label="Сохранить"
          color="primary"
          size="large"
          :disabled="pending"
          :pending="pending"
          @click="submit"
        />
        <observer
          ref="observer"
          class="footer-observer"
          @fullyVisible="fixFooter = false"
          @fullyHidden="fixFooter = true"
        />
      </div>
    </div>

    <template #actions>
      <c-button label="Отменить" size="large" @click="close" />
      <c-button label="Сохранить" color="primary" size="large" :disabled="pending" :pending="pending" @click="submit" />
    </template>
  </c-sidesheet>
</template>

<script>
import CSidesheet from "@/components/scrollable-sidesheet";
import CButton from "@/components/controls2/Button";
import CInput from "@/components/controls2/Input";
import CTextarea from "@/components/controls/textarea";
import FileLoader from "@/components/file-loader";
import Observer from "@/components/IntersectionObserver";

import { isURL } from "@/utils/validate/url";
import { REQUIRED_ERROR } from "@/constants/errorFieldValues";
import { formatCategoryModel } from "../models/CategoryModel";
import { GET_CATEGORIES_ACTION, CREATE_CATEGORY_ACTION, UPDATE_CATEGORY_ACTION } from "../api";
import { Catalog } from "../models/Catalog";

export default {
  components: {
    CSidesheet,
    CButton,
    CInput,
    CTextarea,
    FileLoader,
    Observer,
  },

  props: [],

  data() {
    return {
      active: false,
      isSubmitted: false,
      pending: false,
      loading: true,

      catalog: Catalog(),

      id: null,
      category: null,

      form: {
        name: "",
        parentId: null,
        responsible: null,
        description: "",
        link: "",
      },

      files: [],
      fileLinks: [],

      fixFooter: false,
    };
  },

  computed: {
    title() {
      return this.id ? "Редактировать категорию ТМЦ" : "Новая категория ТМЦ";
    },

    categoriesList() {
      return this.catalog.list.map((category) => ({
        label: category.name,
        value: category.id,
      })).filter((c) => {
       return !this.id || c.value!=this.id
      });
    },

    workersList() {
      return this.$store.getters["actors/workersList"];
    },

    nameError() {
      return this.form.name.trim() ? null : REQUIRED_ERROR;
    },

    linkError() {
      const link = this.form.link.trim();
      if (!link) return null;
      return isURL(link) ? null : "Неверный формат";
    },

    isValid() {
      return !this.nameError && !this.linkError;
    },
  },

  watch: {
    loading(v) {
      if (!v && this.active) {
        this.$nextTick(() => {
          this.$refs.observer.refresh();
        });
      }
    },
  },

  methods: {
    loadCategories() {
      
      this.$store.dispatch(GET_CATEGORIES_ACTION, {}).then((res) => {
        this.catalog.update(res);
        this.loading = false;
      });
    },
    async open(category) {
      this.loading = true;
      this.active = true;

      if (category) {
        this.setData(category);
      }

      this.loadCategories();
    },
    setData(category) {
      const { id, name, parentId, responsible, descriptionModel } = category;
      const { description, link, files } = descriptionModel;

      this.category = category;
      this.id = id;

      this.form.name = name;
      this.form.parentId = parentId ? { value: parentId } : null;
      this.form.responsible = responsible ? { value: +responsible } : null;
      this.form.description = description;
      this.form.link = link;
      this.fileLinks = files;
    },

    close() {
      this.active = false;
      this.reset();
      this.$emit("close");
    },
    getParams() {
      const { name, parentId, responsible, description, link } = this.form;

      const params = formatCategoryModel({
        name,
        parentId: parentId && parentId.value,
        responsible: responsible && responsible.value,
        description,
        link,
        files: this.files,
        oldFiles: this.fileLinks.map((file) => file.id),
      });

      if (this.id) {
        params.id = this.id;
      }

      return params;
    },
    submit() {
      this.isSubmitted = true;
      if (!this.isValid) return;

      this.pending = true;
      const editMode = !!this.id;

      const action = editMode ? UPDATE_CATEGORY_ACTION : CREATE_CATEGORY_ACTION;
      const params = this.getParams();
      console.log("PARAMS", params)
      this.$store.dispatch(action, params).then((res) => {
        if (!res.success) {
          this.$notify({
            group: "error-message",
            data: {
              textMessage: "Произошла ошибка. Попробуйте еще раз",
            },
          });
          return;
        }

        this.$emit(editMode ? "updated" : "created", res.model);
        this.$notify({
          group: "success-message",
          data: {
            textMessage: editMode
              ? `Категория/подкатегория <b>${res.model.name}</b> успешно обновлена`
              : `Категория/подкатегория <b>${res.model.name}</b> успешно добавлена`,
          },
        });
        this.close();
      });
    },
    reset() {
      this.isSubmitted = false;
      this.pending = false;

      this.id = null;
      this.category = null;

      this.form.name = "";
      this.form.parentId = null;
      this.form.responsible = null;
      this.form.description = "";
      this.form.link = "";

      this.files = [];
      this.filesLink = [];

      this.$refs.loader.clear();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars.scss";
@import "~@/assets/styles/grid.scss";

.inventory-category {
  &__actions {
    display: none;
  }

  .note {
    font-size: 11px;
    display: flex;
    align-items: center;
    min-height: 30px;
    padding-top: 20px;
    color: $color-label;
    max-width: 600px;
  }
}

.is-invalid {
  padding-bottom: 24px;
}

@include bootstrapMd {
  .inventory-category {
    @include gridGenerator(11);

    .note {
      padding-top: 0;
      padding-left: 30px;
    }

    &__actions {
      display: flex;
      margin-top: 10px;
      padding-top: 14px;
      padding-bottom: 14px;
      position: sticky;
      bottom: -1px;

      &.fixed {
        background: white;
        z-index: 10;
        &:before {
          content: "";
          position: absolute;
          left: -30px;
          right: -40px;
          top: 0;
          bottom: 0;
          background: white;
          z-index: 0;
          box-shadow: 0px 0px 15px rgba(0, 21, 80, 0.3);
        }
      }

      .footer-observer {
        bottom: -2px;
        position: absolute;
      }
    }
    &__reset {
      margin-right: 10px;
    }
  }

  /deep/ {
    .modal-actions {
      display: none;
    }
  }
}
</style>
